'use client'

import type { Route } from 'next'
import React, { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'

export interface AuthFlashProps {
  /** check = login success (checkmark); exit = sign-out (door + outward arrow). */
  icon: 'check' | 'exit'
  title: string
  subtitle: string
  /**
   * Dwell before completing, and the duration the bottom progress line
   * fills over (`--km-prog-dur`). Default mirrors the prototype's 1.2s.
   */
  delayMs?: number
  /** If set, navigate here once the dwell elapses (after `onComplete`). */
  redirectTo?: Route
  /** Fired once the dwell elapses, before any `redirectTo` navigation. */
  onComplete?: () => void
  /** Test seam — defaults to next/navigation `router.push`. */
  navigate?: <T extends string>(path: Route<T>) => void
}

const DEFAULT_DELAY_MS = 1200

/**
 * Shared login/logout success flash — the `.kiao-login.is-done .km-done`
 * block from the KIAO Voice design (login-modal.html / kiao-app.css):
 * an ink ring with a draw-animated icon, title, subtitle, and a thin
 * progress line that fills over the redirect window. Reduced-motion is
 * handled in CSS (animations off, progress rendered full).
 *
 * Both auth flashes render THIS component — they differ only by `icon`
 * and copy. The host owns the post-dwell action: login closes the modal +
 * refreshes via `onComplete`; logout passes `redirectTo="/"`.
 */
export function AuthFlash({
  icon,
  title,
  subtitle,
  delayMs = DEFAULT_DELAY_MS,
  redirectTo,
  onComplete,
  navigate,
}: AuthFlashProps) {
  const router = useRouter()
  // Hold the latest callbacks behind refs so the one-shot dwell timer
  // (keyed only on delayMs) never restarts when the parent re-renders
  // with fresh callback / router identities.
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const redirectToRef = useRef(redirectTo)
  redirectToRef.current = redirectTo
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  const routerRef = useRef(router)
  routerRef.current = router

  useEffect(() => {
    const timer = window.setTimeout(() => {
      onCompleteRef.current?.()
      const to = redirectToRef.current
      if (to) {
        const push = <T extends string>(path: Route<T>) => routerRef.current.push(path)
        const go = navigateRef.current ?? push
        go(to)
      }
    }, delayMs)
    return () => window.clearTimeout(timer)
  }, [delayMs])

  return (
    <div className="km-done" style={{ ['--km-prog-dur' as string]: `${delayMs}ms` }}>
      <svg className={icon === 'exit' ? 'check exit' : 'check'} viewBox="0 0 52 52" aria-hidden="true">
        <circle cx="26" cy="26" r="24" />
        {icon === 'exit' ? (
          <>
            <path d="M30 17H19a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11" />
            <path d="M25 26h12m-5-5 5 5-5 5" />
          </>
        ) : (
          <path d="M16 27l7 7 13-15" />
        )}
      </svg>
      <h3>{title}</h3>
      <p className="km-sub">{subtitle}</p>
      <div className="km-prog" aria-hidden="true">
        <i />
      </div>
    </div>
  )
}
